import { useCallback, useEffect, useRef, useState } from "react";
import {
  catalogueProductLabel,
  catalogueVersionInForce,
  DOSE_INTERVAL_COPY,
  isAdminRole,
  isOnceAWeek,
  MEDICATION_CATALOGUE_COPY,
  type MedicationCatalogueProduct,
  type MedicationCatalogueVersion,
  type NewMedicationCatalogueVersion,
  WEEKLY_FREQUENCY,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { SourceReviewTasks } from "@/components/SourceReviewTasks";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  approveMedicationCatalogueVersion,
  createMedicationCatalogueVersion,
  fetchMedicationCatalogue,
  markMedicationCatalogueVersionChecked,
} from "@/lib/medication-catalogue-api";

// TES-1292 (TES-1267 §7–8) — "Läkemedelskatalog": the products the doctor
// prescribes from, each with its current version and history.
//   - The admin maintains an entry: the official instruction URL, whether
//     the app may show it, the strengths and intervals, the information
//     owner. Every save is a new version; nothing is edited in place, and a
//     new version is never approved because the one before it was.
//   - The doctor records the clinical approval of the current version. Only
//     then does the order picker require the entry's values for the product.
//   - Either records that the source was checked.
// Every action is audited by the API. The Swedish copy here is proposed.

function fmtDate(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
  });
}

function failText(e: unknown): string {
  return errorText(e instanceof Error ? e.message : String(e));
}

const inputClass =
  "px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary";

/** One value per line; blank lines dropped. */
export function linesOf(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

export function MedicationCatalogueRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [products, setProducts] = useState<MedicationCatalogueProduct[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const isDoctor = claims?.role === "doctor";
  const isAdmin =
    isAdminRole(claims?.role) || (claims?.roles ?? []).some(isAdminRole);

  // Stable, so the review tasks do not refetch on every render.
  const signOutStable = useCallback(() => void signOutRef.current(), []);

  const load = useCallback(async () => {
    setError(null);
    try {
      setProducts((await fetchMedicationCatalogue({ history: true })).products);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(failText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          Läkemedelskatalog
        </h1>
        <p className="text-sm text-textSecondary max-w-2xl">
          Läkemedel, styrkor och intervall som receptfälten väljer från, med
          officiell instruktion per preparat. En ändring sparas som en ny
          version. Valen blir obligatoriska i receptfälten först när läkaren har
          godkänt versionen kliniskt; innan dess anges dos och intervall som
          fritext.
        </p>
      </div>
      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}
      <SourceReviewTasks
        isAdmin={isAdmin}
        onResolved={load}
        onUnauthorized={signOutStable}
      />
      {products === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {products?.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
          Katalogen är tom.
        </div>
      ) : null}
      {products?.map((p) => (
        <ProductCard
          key={p.id}
          product={p}
          isAdmin={isAdmin}
          isDoctor={isDoctor}
          onChanged={load}
          onUnauthorized={() => void signOutRef.current()}
        />
      ))}
    </div>
  );
}

function ProductCard({
  product,
  isAdmin,
  isDoctor,
  onChanged,
  onUnauthorized,
}: {
  product: MedicationCatalogueProduct;
  isAdmin: boolean;
  isDoctor: boolean;
  onChanged: () => Promise<void>;
  onUnauthorized: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = product.current;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      if (e instanceof UnauthorizedError) return onUnauthorized();
      setError(failText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border border-surfaceMuted rounded-md bg-surface p-md flex flex-col gap-sm"
      data-testid={`catalogue-${product.key}`}
    >
      <div className="flex flex-wrap items-baseline gap-sm">
        <h2 className="font-display text-xl">
          {catalogueProductLabel(product)}
        </h2>
        <span className="text-sm text-textSecondary">
          {product.activeSubstance}
        </span>
        {product.active ? null : (
          <span className="text-xs rounded-sm bg-surfaceMuted px-xs">
            Inaktiv
          </span>
        )}
        <span className="text-xs text-textSecondary">Version {v.version}</span>
        <ApprovalBadge version={v} />
      </div>
      <VersionDetails version={v} />
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="flex flex-wrap gap-sm">
        {isDoctor && !v.clinicallyApprovedAt ? (
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void act(() => approveMedicationCatalogueVersion(v.id))
            }
            className="rounded-md bg-brand px-md py-xs text-sm text-white disabled:opacity-50"
          >
            Godkänn version {v.version} kliniskt
          </button>
        ) : null}
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void act(() => markMedicationCatalogueVersionChecked(v.id))
          }
          className="rounded-md border border-surfaceMuted px-md py-xs text-sm disabled:opacity-50"
        >
          Markera källan som kontrollerad
        </button>
        {isAdmin && !editing ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(true)}
            className="rounded-md border border-surfaceMuted px-md py-xs text-sm disabled:opacity-50"
          >
            Ändra (ny version)
          </button>
        ) : null}
      </div>
      {editing ? (
        <VersionForm
          current={v}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={(body) =>
            act(async () => {
              await createMedicationCatalogueVersion(product.id, body);
              setEditing(false);
            })
          }
        />
      ) : null}
      {product.history && product.history.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-textSecondary">
            Tidigare versioner ({product.history.length})
          </summary>
          <div className="mt-sm flex flex-col gap-sm">
            {product.history.map((h) => (
              <div
                key={h.id}
                className="border-t border-surfaceMuted pt-sm flex flex-col gap-xs"
              >
                <span className="text-xs text-textSecondary">
                  Version {h.version} · ersatt {fmtDate(h.supersededAt)}
                </span>
                <VersionDetails version={h} />
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}

function ApprovalBadge({ version }: { version: MedicationCatalogueVersion }) {
  if (catalogueVersionInForce(version)) {
    return (
      <span className="text-xs rounded-sm bg-success/10 text-success px-xs">
        Kliniskt godkänd {fmtDate(version.clinicallyApprovedAt)}
      </span>
    );
  }
  return (
    <span className="text-xs rounded-sm bg-warning/10 text-warning px-xs">
      {version.clinicallyApprovedAt
        ? "Godkänd, men saknar styrkor eller intervall"
        : MEDICATION_CATALOGUE_COPY.notApproved}
    </span>
  );
}

function VersionDetails({
  version: v,
}: {
  version: MedicationCatalogueVersion;
}) {
  const row = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-[12rem_1fr] gap-sm">
      <dt className="text-textSecondary">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
  return (
    <dl className="flex flex-col gap-xs text-sm">
      {row(
        "Styrkor / doser",
        v.strengths.length ? v.strengths.join(", ") : "Inga angivna",
      )}
      {row(
        "Intervall",
        v.intervals.length ? v.intervals.join(", ") : "Inga angivna",
      )}
      {row(
        "Officiell instruktion",
        v.instructionUrl ? (
          <span>
            <a
              href={v.instructionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:underline break-all"
            >
              {v.instructionUrl}
            </a>{" "}
            · {v.instructionUrlActive ? "visas i appen" : "avstängd"}
          </span>
        ) : (
          "Ingen länk (avstängd)"
        ),
      )}
      {v.filmUrl ? row("Instruktionsfilm", v.filmUrl) : null}
      {row("Avsändare", v.sourceSender ?? "–")}
      {row("Språk", v.language)}
      {row("Informationsägare", v.informationOwner ?? "–")}
      {row("Källa för styrkor och intervall", v.contentSource ?? "–")}
      {row("Senast kontrollerad", fmtDate(v.lastCheckedAt))}
      {v.changeNote ? row("Ändringsnotering", v.changeNote) : null}
    </dl>
  );
}

function VersionForm({
  current,
  busy,
  onCancel,
  onSave,
}: {
  current: MedicationCatalogueVersion;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: NewMedicationCatalogueVersion) => Promise<void>;
}) {
  const [strengths, setStrengths] = useState(current.strengths.join("\n"));
  // TES-1375 (Ö-419) — the interval is a fixed choice: once a week is the
  // only one Ella 2.1 supports, so the entry either offers it or offers none.
  const [weekly, setWeekly] = useState(current.intervals.some(isOnceAWeek));
  const [url, setUrl] = useState(current.instructionUrl ?? "");
  const [urlActive, setUrlActive] = useState(current.instructionUrlActive);
  const [filmUrl, setFilmUrl] = useState(current.filmUrl ?? "");
  const [sender, setSender] = useState(current.sourceSender ?? "");
  const [owner, setOwner] = useState(current.informationOwner ?? "");
  const [contentSource, setContentSource] = useState(
    current.contentSource ?? "",
  );
  const [changeNote, setChangeNote] = useState("");
  const urlMissing = urlActive && !url.trim();
  const canSave = changeNote.trim() !== "" && !urlMissing && !busy;
  const text = (
    label: string,
    value: string,
    set: (v: string) => void,
    multiline = false,
  ) => (
    <label className="flex flex-col gap-xs text-sm">
      {label}
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => set(e.target.value)}
          rows={4}
          className={inputClass}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => set(e.target.value)}
          className={inputClass}
        />
      )}
    </label>
  );
  return (
    <form
      className="flex flex-col gap-sm border-t border-surfaceMuted pt-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        void onSave({
          strengths: linesOf(strengths),
          intervals: weekly ? [WEEKLY_FREQUENCY] : [],
          instructionUrl: url.trim() || null,
          instructionUrlActive: urlActive,
          filmUrl: filmUrl.trim() || null,
          sourceSender: sender.trim() || null,
          informationOwner: owner.trim() || null,
          contentSource: contentSource.trim() || null,
          changeNote: changeNote.trim(),
        });
      }}
    >
      <p className="text-xs text-textSecondary">
        Den nya versionen är inte kliniskt godkänd förrän läkaren har godkänt
        den.
      </p>
      <div className="grid gap-sm md:grid-cols-2">
        {text("Styrkor / doser (en per rad)", strengths, setStrengths, true)}
        <fieldset className="flex flex-col gap-xs text-sm">
          <legend>Intervall</legend>
          <label className="flex items-center gap-xs">
            <input
              type="checkbox"
              checked={weekly}
              onChange={(e) => setWeekly(e.target.checked)}
            />
            {WEEKLY_FREQUENCY}
          </label>
          <p className="text-xs text-textSecondary">
            {DOSE_INTERVAL_COPY.hint}
          </p>
        </fieldset>
      </div>
      {text("Officiell instruktion (https://…)", url, setUrl)}
      <label className="flex items-center gap-xs text-sm">
        <input
          type="checkbox"
          checked={urlActive}
          onChange={(e) => setUrlActive(e.target.checked)}
        />
        Visa länken i appen
      </label>
      {urlMissing ? (
        <p className="text-xs text-danger">En aktiv länk kräver en adress.</p>
      ) : null}
      {text("Instruktionsfilm (https://…, valfri)", filmUrl, setFilmUrl)}
      {text("Avsändare", sender, setSender)}
      {text("Informationsägare", owner, setOwner)}
      {text(
        "Källa för styrkor och intervall",
        contentSource,
        setContentSource,
        true,
      )}
      {text("Varför ändras posten? (krävs)", changeNote, setChangeNote)}
      <div className="flex gap-sm">
        <button
          type="submit"
          disabled={!canSave}
          className="rounded-md bg-brand px-md py-xs text-sm text-white disabled:opacity-50"
        >
          Spara ny version
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-surfaceMuted px-md py-xs text-sm"
        >
          Avbryt
        </button>
      </div>
    </form>
  );
}
